/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import $ from 'jquery';
//  @ts-ignore
import request from 'js-sdk/src/common/transport/request';
import jrsConfigs from 'js-sdk/src/jrs.configs'
//  @ts-ignore
import javaPropertiesParser from 'js-sdk/src/common/util/parse/javaProperties';
// @ts-ignore
import biComponentErrorFactory from 'js-sdk/src/common/bi/error/biComponentErrorFactory';
import { IMetadataService } from './ServicesTypes';

const DEFAULT_LOCALE = '';

type MetadataExecution = {
    server: string,
    resource: string,
    deferred: JQueryDeferred<any>
}

function convertUnicode(text: string) {
    return text.replace(/\\u(\w\w\w\w)/g, (fullMatch, match) => {
        const charcode = parseInt(match, 16);
        return String.fromCharCode(charcode);
    });
}

function extractBundles(data: any): any[] {
    let res = data.bundles || [];
    if (data && data.dataSource && data.dataSource.semanticLayerDataSource && data.dataSource.semanticLayerDataSource.bundles) {
        res = data.dataSource.semanticLayerDataSource.bundles;
    }

    return res;
}

function loadBundles(server: string, data: any) {
    const deferred = $.Deferred();
    const newData = data;
    const bundles = extractBundles(data);

    if (data && bundles.length) {
        let { userLocale } = jrsConfigs;

        let bundle = bundles.find((bundleObj: any) => {
            return bundleObj.locale === userLocale;
        });

        if (!bundle) {
            [userLocale] = userLocale.split('_');
            bundle = bundles.find((bundleObj: any) => {
                return bundleObj.locale === userLocale;
            });
        }

        if (!bundle) {
            bundle = bundles.find((bundleObj: any) => {
                return bundleObj.locale === DEFAULT_LOCALE;
            });
        }

        if (bundle && server) {
            const { uri } = (bundle.file.file || bundle.file.fileReference);

            request({
                url: server.concat('/rest_v2/resources', uri)
            }).then((bundleAsString: string) => {
                const convertedBundles = convertUnicode(bundleAsString);
                newData.bundle = javaPropertiesParser(convertedBundles);
                deferred.resolve(newData);
            }, (error: any) => {
                deferred.reject(error);
            });
        }
    } else {
        deferred.resolve(newData);
    }

    return deferred.promise();
}

function onMetadataFetched(data: any, xhr: any) {
    let error;
    const contentType = xhr.getResponseHeader('Content-Type')?.toLowerCase(),
        isAdHocViewResource = contentType?.startsWith('application/repository.adhocdataview');
    if (!isAdHocViewResource) {
        error = {
            message: `Resource ${data.uri} is not an Ad Hoc View.`,
            errorCode: 'resource.not.adhoc',
            parameters: [data.uri]
        };
    } else if (isAdHocViewResource && !Object.prototype.hasOwnProperty.call(data, 'query')) {
        // Data.query has been set to undefined during parse, but it still exist as a property. For OLAP based views it is not present at all.
        error = {
            message: 'OLAP based Ad Hoc view is not supported',
            errorCode: 'resource.is.olap.adhoc',
            parameters: [data.uri]
        };
    }
    return error;
}

export default class MetadataService implements IMetadataService {
    executions: MetadataExecution[]

    constructor() {
        this.executions = [];
    }

    reset() {
        this.executions = [];
        return $.Deferred().resolve();
    }

    loadMetadata(server: string, resource: string): JQueryDeferred<any> {
        if (!server || !resource) {
            return $.Deferred();
        }

        let execution = this.executions.find((ex) => (ex.server === server && ex.resource === resource))

        if (!execution) {
            execution = {
                server,
                resource,
                deferred: $.Deferred()
            }

            this.executions.push(execution);

            request({
                headers: {
                    Accept: 'application/json'
                },
                url: server.concat('/rest_v2/resources', resource[0] === '/' ? resource : `/${resource}`, '?expanded=true')
            }).then((data: any, result: any, xhr: any) => {
                const error = onMetadataFetched(data, xhr);
                if (error) {
                    return execution!.deferred.reject(error);
                }
                return loadBundles(server, data);
            }).then((dataWithBundles: any) => {
                execution!.deferred.resolve(dataWithBundles);
            }).fail((error: any) => {
                execution!.deferred.reject(biComponentErrorFactory.requestError(error));
            });
        }

        return execution.deferred;
    }
}
